/**
 * Softys · Toallitas Húmedas — el estado de una jornada de mercado.
 *
 * 🔴 POR QUÉ ESTO EXISTE Y NO ES UN BOOLEANO.
 *
 * La bandeja muestra lo PROGRAMADO, no solo lo registrado: es el pedido del usuario —*«si mañana
 * tenemos diez mercados, tenemos que tener los espacios de los diez mercados ahí»*—. Pero una
 * lista de lo programado **sin estado miente**, y ya mintió en este proyecto: en el tablero del
 * cliente los turnos EN CURSO se pintaban como «sin registro», y el defecto quedó abierto como DB1.
 *
 * Un mercado de mañana sin visitas no es una deuda, es el futuro. Uno de hoy a media jornada
 * tampoco. Uno de anteayer sin una sola visita, sí. Pintar los tres igual enseña a ignorar el
 * rojo, que es la forma más cara de perder una alerta.
 *
 * Vive acá, puro y sin React, para poder comprobarlo caso por caso sin montar la pantalla.
 */

export type EstadoJornadaSoftys =
  /** Se registró al menos un puesto. */
  | 'con_visitas'
  /** La jornada pasó y no entró nada. **Lo único que cuenta como deuda.** */
  | 'sin_visitas'
  /** Es hoy y todavía no registró nada: puede estar caminando al mercado. */
  | 'en_curso'
  /** La fecha todavía no llegó. */
  | 'futuro'
  /** La posición está programada pero nadie la tiene aprobada. */
  | 'sin_asignar';

export const ESTADO_JORNADA_LABEL: Record<EstadoJornadaSoftys, string> = {
  con_visitas: 'Con visitas',
  // «Aun sin registro» y no «Sin visitas»: lo segundo se lee como un hecho de campo —que la
  // persona no visito ningun puesto— cuando lo que dice es que no llego NADA al sistema. Son
  // cosas distintas y la segunda es la que hay que perseguir. Pedido del usuario, 17-set.
  sin_visitas: 'Aún sin registro',
  en_curso: 'En curso',
  futuro: 'Programado',
  sin_asignar: 'Sin asignar',
};

/**
 * El color va por lo que significa, no por lo que llama la atención.
 *
 * Solo la deuda real es roja. Si «programado» también lo fuera, un lunes con toda la semana
 * cargada abriría la bandeja en rojo entero y nadie volvería a mirarla.
 */
export const ESTADO_JORNADA_CLS: Record<EstadoJornadaSoftys, string> = {
  con_visitas: 'bg-green-100 text-green-800',
  sin_visitas: 'bg-red-100 text-red-800',
  en_curso: 'bg-blue-100 text-blue-800',
  futuro: 'bg-gray-100 text-gray-600',
  sin_asignar: 'bg-amber-100 text-amber-800',
};

export interface JornadaParaEstado {
  fecha: string;
  assignmentId: string | null;
  visitasComerciante: number;
  visitasVenta: number;
}

/**
 * En qué estado está una jornada. `hoy` se pasa —no se lee del reloj— para que las pruebas no
 * dependan del día en que se corran, y para que la fecha sea siempre la de Lima.
 *
 * El ORDEN de las preguntas importa: lo registrado gana sobre todo lo demás. Una jornada sin
 * asignación aprobada en la que igual se registraron visitas es un dato real y no un hueco — y
 * esconderlo detrás de «sin asignar» perdería trabajo que sí se hizo.
 */
export function estadoDeJornadaSoftys(j: JornadaParaEstado, hoy: string): EstadoJornadaSoftys {
  if (j.visitasComerciante + j.visitasVenta > 0) return 'con_visitas';
  if (j.fecha > hoy) return 'futuro';
  if (j.assignmentId === null) return 'sin_asignar';
  if (j.fecha === hoy) return 'en_curso';
  return 'sin_visitas';
}

/** Lo único que hay que perseguir. Existe como función para que la casilla «solo pendientes» y
 *  las tarjetas de resumen no puedan discrepar sobre qué es una deuda. */
export function esDeudaDeJornada(e: EstadoJornadaSoftys): boolean {
  return e === 'sin_visitas';
}
